/**
 * src/components/spatial/SpatialEventCard.tsx
 *
 * An event, on the map, answering the questions people ask about one.
 *
 * What is this, where is it, when, is it on, is anything live, how
 * many people are here, what can I do, can I watch, what sessions are
 * inside it. The card reads all of that from `buildEventSummary`,
 * which reads it from the server — no clock comparison, no eligibility
 * arithmetic and no attendance guessed at anywhere in this file.
 *
 * It is a `MapEntityTooltip` like the chat, listen and deposit cards,
 * so it takes its place in the same surface ranking rather than
 * floating above everything: an event panel that covered the country
 * tooltip would be a new overlap to manage instead of a reuse of the
 * one that exists. Its own width is capped against the viewport the
 * way the sibling cards are, which is what makes it usable on a phone.
 *
 * Every camera move goes through `mapActions`. There is no map
 * instance in scope here and no pixel arithmetic, which is the same
 * rule the chat card follows.
 *
 * The operator controls render from `operatorControlsFor` and are
 * disabled with a reason rather than hidden. That is a courtesy, not a
 * permission: the server authorizes every one of these calls against
 * the event's authority model and will refuse regardless of what this
 * card decided to draw.
 */

import React, { useCallback, useEffect, useState } from 'react';

import { MapEntityTooltip } from '../map/MapEntityTooltip';
import {
  getEvent,
  getEventPresence,
  getEventPresenceHistory,
  listEvents,
  runEventLifecycle,
  type EventDetail,
  type EventLifecycleVerb,
  type EventPresenceCounts,
} from '../../services/spatial/spatialEventsClient';
import {
  buildEventSummary,
  canWatch,
  describePresence,
  describeWatch,
  describeWhen,
  type EventActionDescriptor,
  type EventArchivedBroadcast,
  type EventChild,
  type EventSummary,
} from './eventSummaryModel';
import {
  explainAction,
  hasTechnicalDetail,
  operatorControlsFor,
  type EventOperatorAction,
} from './eventManagementModel';
import type { MapActions } from '../../services/spatial/mapActions';

const CARD_WIDTH = 340;

export interface SpatialEventCardProps {
  eventObjectId: string;
  /** Where on the map the card hangs, in container pixels. */
  anchor: { x: number; y: number };
  viewport?: { width: number; height: number } | null;
  accentColor?: string;
  /** The only way this card is allowed to touch the map. */
  mapActions?: MapActions | null;
  /** The event's geometry, when the map already has it. */
  geometry?: unknown;
  wallet?: string | null;
  /** Whether to draw the operator half at all. Server still decides. */
  canManage?: boolean;
  /** Actions from `get_nearby_actions`, which the AI reads too. */
  actions?: readonly EventActionDescriptor[] | null;
  onWatch?: (input: { broadcastId: string | null; live: boolean }) => void;
  onEdit?: (eventObjectId: string) => void;
  onClose: () => void;
}

const labelStyle: React.CSSProperties = {
  fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, color: '#64748b',
};
const noteStyle: React.CSSProperties = { fontSize: 11, color: '#94a3b8', lineHeight: 1.5 };
const rowStyle: React.CSSProperties = {
  display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
};
const buttonStyle: React.CSSProperties = {
  border: 'none', borderRadius: 8, padding: '5px 10px', fontSize: 11,
  fontWeight: 700, background: 'rgba(99,102,241,0.3)', color: '#818cf8',
  cursor: 'pointer',
};
const quietButtonStyle: React.CSSProperties = {
  ...buttonStyle, background: 'rgba(148,163,184,0.14)', color: '#cbd5e1',
};
const disabledButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  background: 'rgba(148,163,184,0.08)', color: '#64748b', cursor: 'not-allowed',
};

/** The three readings, shown apart because none of them defines another. */
const StateLine: React.FC<{ summary: EventSummary }> = ({ summary }) => (
  <div style={rowStyle}>
    <span style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0' }}>
      {describeWhen(summary)}
    </span>
    {summary.isBroadcasting.known && summary.isBroadcasting.value && (
      <span
        style={{
          fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
          background: 'rgba(239,68,68,0.2)', color: '#f87171',
          borderRadius: 4, padding: '1px 6px',
        }}
      >
        LIVE
      </span>
    )}
    {summary.liveSource === 'host' && (
      /* Worth saying: the venue is broadcasting, not the event. */
      <span style={labelStyle}>via the venue</span>
    )}
  </div>
);

/** One action, in the order a person asks about it. */
const ActionRow: React.FC<{ action: EventActionDescriptor }> = ({ action }) => {
  const explained = explainAction(action);
  if (!explained) return null;

  return (
    <div
      style={{
        borderTop: '1px solid rgba(148,163,184,0.12)', paddingTop: 6,
        display: 'flex', flexDirection: 'column', gap: 2,
      }}
    >
      <div style={rowStyle}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0' }}>
          {explained.action}
        </span>
        <span
          style={{
            ...labelStyle,
            color: explained.availability.ok ? '#4ade80' : '#f59e0b',
          }}
        >
          {explained.availability.reason}
        </span>
      </div>
      {(explained.conditions.spatial || explained.conditions.temporal) && (
        <div style={noteStyle}>
          {[explained.conditions.spatial, explained.conditions.temporal]
            .filter(Boolean).join(' · ')}
        </div>
      )}
      {explained.functionName && (
        <div style={noteStyle}>
          Calls <code>{explained.functionName}</code>
          {explained.signing.required ? ' · needs your signature' : ''}
          {' · '}{explained.signing.cost}
        </div>
      )}
      {hasTechnicalDetail(explained) && (
        /* Last, folded, and reachable — somebody debugging a rule
           needs the identifiers, and nobody else does. */
        <details>
          <summary style={{ ...labelStyle, cursor: 'pointer' }}>Technical detail</summary>
          <div style={{ ...noteStyle, wordBreak: 'break-all', paddingTop: 4 }}>
            {explained.technical.contractName && (
              <div>Contract: {explained.technical.contractName}</div>
            )}
            {explained.technical.contractAddress && (
              <div><code>{explained.technical.contractAddress}</code></div>
            )}
            {explained.technical.network && <div>Network: {explained.technical.network}</div>}
            {explained.technical.executionRuleId && (
              <div>Execution rule #{explained.technical.executionRuleId}</div>
            )}
            {explained.technical.parameters && (
              <div>Arguments: {explained.technical.parameters}</div>
            )}
          </div>
        </details>
      )}
    </div>
  );
};

export const SpatialEventCard: React.FC<SpatialEventCardProps> = ({
  eventObjectId,
  anchor,
  viewport = null,
  accentColor = '#a855f7',
  mapActions = null,
  geometry = null,
  wallet = null,
  canManage = false,
  actions = null,
  onWatch,
  onEdit,
  onClose,
}) => {
  const [detail, setDetail] = useState<EventDetail | null>(null);
  const [presence, setPresence] = useState<EventPresenceCounts | null>(null);
  const [broadcasts, setBroadcasts] = useState<EventArchivedBroadcast[] | null>(null);
  const [children, setChildren] = useState<EventChild[] | null>(null);
  const [history, setHistory] = useState<{ total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<EventOperatorAction | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);

    (async () => {
      const answer = await getEvent(eventObjectId);
      if (cancelled) return;
      if (!answer.ok) {
        // The refusal is the server's, reported rather than rephrased:
        // a person told "not found" when they meant "not allowed" will
        // keep trying.
        setError(answer.reason === 'not_found'
          ? 'That event is not here any more.'
          : 'Could not load this event.');
        return;
      }
      setDetail(answer as EventDetail);
    })();

    return () => { cancelled = true; };
  }, [eventObjectId, nonce]);

  // Asked for separately, because each is a different question with a
  // different failure. A missing count should not blank the event.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [counts, list] = await Promise.all([
        getEventPresence(eventObjectId),
        listEvents({ parentEventId: eventObjectId, includeLive: true }),
      ]);
      if (cancelled) return;
      if (counts.ok) setPresence(counts as unknown as EventPresenceCounts);
      if (list.ok) {
        setChildren(((list as unknown as { events?: EventChild[] }).events) || []);
      }
    })();
    return () => { cancelled = true; };
  }, [eventObjectId, nonce]);

  // The archive half. Only asked for once the event is not live, since
  // a running event's history is the thing nobody is looking at.
  useEffect(() => {
    if (!detail) return undefined;
    let cancelled = false;
    (async () => {
      const response = await fetch(
        `${window.location.origin}/api/v2/spatial-events/${encodeURIComponent(eventObjectId)}/broadcasts`,
      ).then((r) => r.json()).catch(() => null);
      if (cancelled || !response?.ok) return;
      setBroadcasts(response.broadcasts || []);
    })();
    return () => { cancelled = true; };
  }, [detail, eventObjectId, nonce]);

  // Only for an event that is over: "how many came" is a question
  // about the past, and asking it of a running event invites a reader
  // to mistake it for the live figure.
  useEffect(() => {
    if (!detail?.lifecycle) return undefined;
    if (!['completed', 'archived', 'cancelled'].includes(detail.lifecycle)) return undefined;
    let cancelled = false;
    (async () => {
      const answer = await getEventPresenceHistory(eventObjectId);
      if (!cancelled && answer.ok) setHistory(answer as unknown as { total: number });
    })();
    return () => { cancelled = true; };
  }, [detail?.lifecycle, eventObjectId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const summary = buildEventSummary({
    detail, presence, actions, broadcasts, children,
  });

  const runLifecycle = useCallback(async (verb: EventLifecycleVerb) => {
    setBusy(verb as EventOperatorAction);
    const answer = await runEventLifecycle(eventObjectId, verb, { wallet: wallet || undefined });
    setBusy(null);
    if (!answer.ok) {
      // The server's word, shown as the server's word. A control this
      // card enabled can still be refused, and that refusal is the
      // authoritative one.
      setError(answer.reason === 'not_authorized'
        ? 'You are not allowed to do that to this event.'
        : `That did not work: ${answer.reason}`);
      return;
    }
    setError(null);
    setNonce((n) => n + 1);
  }, [eventObjectId, wallet]);

  const LIFECYCLE_VERBS: readonly string[] = [
    'pause', 'resume', 'cancel', 'complete', 'archive',
  ];

  return (
    <MapEntityTooltip
      data-testid="spatial-event-card"
      anchor={anchor}
      viewport={viewport}
      surfaceId={`event-card-${eventObjectId}`}
      /* Already in the ladder, and already counted as a surface that
         occupies attention — so on a phone it competes with the chat
         and listen cards rather than stacking on top of them. */
      surfaceKind="event_panel"
      width={CARD_WIDTH}
      estimatedHeight={summary ? 320 : 120}
      accentColor={accentColor}
      title={summary?.title || 'Event'}
      badge={(
        <button
          type="button"
          aria-label="Close event"
          onClick={onClose}
          style={{
            border: 'none', background: 'rgba(255,255,255,0.1)', color: '#94a3b8',
            borderRadius: 4, padding: '1px 6px', fontSize: 12, cursor: 'pointer',
          }}
        >
          ✕
        </button>
      )}
    >
      <div
        style={{
          padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8,
          maxHeight: 'min(70vh, 520px)', overflowY: 'auto',
        }}
      >
        {error && <div style={{ ...noteStyle, color: '#f87171' }}>{error}</div>}

        {!summary && !error && <div style={noteStyle}>Loading…</div>}

        {summary && (
          <>
            <StateLine summary={summary} />

            {summary.description && <div style={noteStyle}>{summary.description}</div>}

            <div style={noteStyle}>
              {[summary.venue, summary.floorRef, summary.zoneRef]
                .filter(Boolean).join(' · ')}
              {summary.startsAt && (
                <>
                  <br />
                  {new Date(summary.startsAt).toLocaleString()}
                  {summary.timezone ? ` (${summary.timezone})` : ''}
                </>
              )}
            </div>

            {/* Only when known. An absent count says nothing rather
                than saying nobody came. */}
            {describePresence(summary) && (
              <div style={rowStyle}>
                <span style={labelStyle}>Here now</span>
                <span style={{ fontSize: 12, color: '#e2e8f0' }}>
                  {describePresence(summary)}
                </span>
              </div>
            )}

            {history && (
              <div style={rowStyle}>
                <span style={labelStyle}>Attended</span>
                <span style={{ fontSize: 12, color: '#e2e8f0' }}>{history.total}</span>
              </div>
            )}

            <div style={rowStyle}>
              {/* Every one of these is an intent. None of them knows
                  where on the screen anything is. */}
              <button
                type="button"
                style={quietButtonStyle}
                onClick={() => mapActions?.fitEventArea({ geometry })}
              >
                Show the area
              </button>
              {summary.zoneRef && (
                <button
                  type="button"
                  style={quietButtonStyle}
                  onClick={() => mapActions?.showEventArea({
                    kind: 'stage', objectId: eventObjectId, geometry,
                  })}
                >
                  Show the stage
                </button>
              )}
              {canWatch(summary) && (
                <button
                  type="button"
                  style={buttonStyle}
                  onClick={() => onWatch?.({
                    live: summary.watch.liveNow,
                    broadcastId: summary.watch.liveNow
                      ? null
                      : summary.watch.playable[0]?.broadcastId || null,
                  })}
                >
                  {summary.watch.liveNow ? 'Watch live' : 'Watch recording'}
                </button>
              )}
            </div>

            {describeWatch(summary) && (
              <div style={noteStyle}>{describeWatch(summary)}</div>
            )}

            {summary.children.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={labelStyle}>Inside this event</span>
                {summary.children.map((child) => (
                  <button
                    key={child.objectId}
                    type="button"
                    style={{ ...quietButtonStyle, textAlign: 'left' }}
                    onClick={() => mapActions?.showChildEvent({
                      objectId: child.objectId,
                    })}
                  >
                    {child.event?.title || child.name || child.objectId}
                  </button>
                ))}
              </div>
            )}

            {(summary.actions.available.length > 0 || summary.actions.blocked.length > 0) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={labelStyle}>What you can do here</span>
                {summary.actions.available.map((action, i) => (
                  <ActionRow key={action.id || `${action.verb}-${i}`} action={action} />
                ))}
                {summary.actions.blocked.map((action, i) => (
                  <ActionRow key={action.id || `blocked-${action.verb}-${i}`} action={action} />
                ))}
              </div>
            )}

            {canManage && (
              <details open={false}>
                <summary style={{ ...labelStyle, cursor: 'pointer' }}>
                  Manage this event
                </summary>
                <div
                  style={{
                    display: 'flex', flexWrap: 'wrap', gap: 6, paddingTop: 6,
                  }}
                >
                  {operatorControlsFor(summary).map((control) => {
                    const isLifecycle = LIFECYCLE_VERBS.includes(control.action);
                    const handler = control.action === 'edit'
                      ? () => onEdit?.(eventObjectId)
                      : (isLifecycle
                        ? () => void runLifecycle(control.action as EventLifecycleVerb)
                        : undefined);

                    return (
                      <button
                        key={control.action}
                        type="button"
                        disabled={!control.applicable || !handler || busy === control.action}
                        title={control.unavailableReason || control.detail}
                        style={control.applicable && handler
                          ? (control.destructive
                            ? { ...quietButtonStyle, color: '#fca5a5' }
                            : quietButtonStyle)
                          : disabledButtonStyle}
                        onClick={handler}
                      >
                        {control.label}
                        {control.needsHostAuthority ? ' ·' : ''}
                      </button>
                    );
                  })}
                </div>
                <div style={{ ...noteStyle, paddingTop: 6 }}>
                  · needs the venue to agree. Whether you may do any of
                  these is decided by the server, not by this panel.
                </div>
              </details>
            )}
          </>
        )}
      </div>
    </MapEntityTooltip>
  );
};

export default SpatialEventCard;
